import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { User, Star, Check, X, Search, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Popular assets to show by default
const POPULAR_CRYPTO = ['BTC-USD', 'ETH-USD', 'SOL-USD', 'XRP-USD', 'ADA-USD', 'DOGE-USD', 'DOT-USD', 'AVAX-USD', 'LINK-USD', 'MATIC-USD'];
const POPULAR_FOREX = ['EUR/USD', 'GBP/USD', 'USD/JPY', 'AUD/USD', 'USD/CAD', 'USD/CHF', 'NZD/USD', 'EUR/GBP', 'EUR/JPY', 'GBP/JPY'];

export default function Perfil() {
  const { token, user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/me/favorites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorite_assets || []);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 1) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/instruments/search?q=${encodeURIComponent(query)}&type=all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      }
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setSearching(false);
    }
  };

  const toggleFavorite = (symbol) => {
    if (favorites.includes(symbol)) {
      setFavorites(favorites.filter(f => f !== symbol));
    } else if (favorites.length < 5) {
      setFavorites([...favorites, symbol]);
    } else {
      toast.error('Máximo 5 activos favoritos');
    }
  };

  const saveFavorites = async () => {
    if (favorites.length < 3) {
      toast.error('Selecciona al menos 3 activos favoritos');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/users/me/favorites`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ favorite_assets: favorites })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al guardar');
      }

      toast.success('Favoritos guardados correctamente');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const renderAssetButton = (symbol, type) => {
    const isSelected = favorites.includes(symbol);
    return (
      <button
        key={symbol}
        onClick={() => toggleFavorite(symbol)}
        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
          isSelected
            ? 'bg-[hsl(198,62%,54%)] text-white'
            : 'bg-white/5 text-white/70 hover:bg-white/10'
        }`}
      >
        {isSelected && <Check className="w-4 h-4" />}
        {symbol}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white/50" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 rounded-full bg-[hsl(198,62%,54%)]/20 flex items-center justify-center">
          <User className="w-8 h-8 text-[hsl(198,62%,54%)]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">{user?.name}</h1>
          <p className="text-white/50">{user?.email}</p>
        </div>
      </div>

      {/* Favorites Section */}
      <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Star className="w-6 h-6 text-[hsl(45,93%,47%)]" />
          <h2 className="text-xl font-semibold text-white">Mis Activos Favoritos</h2>
        </div>
        
        <p className="text-white/60 mb-6">
          Selecciona <strong className="text-white">3 a 5 activos</strong> para usar en sesiones Personalizada y Avanzada. 
          El sistema elegirá uno aleatoriamente sin que sepas cuál hasta después de predecir.
        </p>

        {/* Current selection */}
        <div className="mb-6 p-4 bg-white/5 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-white/60">Seleccionados: {favorites.length}/5</p>
            <div className="flex gap-1">
              {[1,2,3,4,5].map(i => (
                <div 
                  key={i}
                  className={`w-3 h-3 rounded-full ${
                    i <= favorites.length ? 'bg-[hsl(198,62%,54%)]' : 'bg-white/10'
                  }`}
                />
              ))}
            </div>
          </div>
          
          {favorites.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {favorites.map(symbol => (
                <div 
                  key={symbol}
                  className="flex items-center gap-2 px-3 py-2 bg-[hsl(198,62%,54%)]/20 border border-[hsl(198,62%,54%)]/30 rounded-lg"
                >
                  <span className="text-white font-medium">{symbol}</span>
                  <button
                    onClick={() => toggleFavorite(symbol)}
                    className="text-white/50 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white/40 text-center py-2">No has seleccionado ningún favorito</p>
          )}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Buscar activo (BTC, EUR, SOL...)"
              className="w-full bg-[hsl(232,36%,12%)] border border-[hsl(232,18%,18%)] rounded-xl py-3 pl-11 pr-4 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[hsl(198,62%,54%)]/50"
            />
            {searching && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40 animate-spin" />
            )}
          </div>
          
          {searchResults.length > 0 && (
            <div className="mt-2 max-h-48 overflow-y-auto bg-[hsl(232,36%,12%)] border border-[hsl(232,18%,18%)] rounded-xl divide-y divide-white/5">
              {searchResults.map(item => (
                <button
                  key={item.symbol}
                  onClick={() => {
                    toggleFavorite(item.symbol);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  className={`w-full p-3 text-left hover:bg-white/5 flex items-center justify-between ${
                    favorites.includes(item.symbol) ? 'bg-[hsl(198,62%,54%)]/10' : ''
                  }`}
                >
                  <div>
                    <p className="text-white font-medium">{item.symbol}</p>
                    <p className="text-white/50 text-sm">{item.name}</p>
                  </div>
                  {favorites.includes(item.symbol) && (
                    <Check className="w-5 h-5 text-[hsl(198,62%,54%)]" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Popular Crypto */}
        <div className="mb-4">
          <p className="text-sm text-white/50 mb-2">Criptomonedas populares</p>
          <div className="flex flex-wrap gap-2">
            {POPULAR_CRYPTO.map(symbol => renderAssetButton(symbol, 'crypto'))}
          </div>
        </div>

        {/* Popular Forex */}
        <div className="mb-6">
          <p className="text-sm text-white/50 mb-2">Forex populares</p>
          <div className="flex flex-wrap gap-2">
            {POPULAR_FOREX.map(symbol => renderAssetButton(symbol, 'forex'))}
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={saveFavorites}
          disabled={saving || favorites.length < 3}
          className={`w-full py-4 rounded-xl font-medium text-lg transition-all ${
            favorites.length >= 3 && !saving
              ? 'bg-[hsl(198,62%,54%)] hover:bg-[hsl(198,62%,48%)] text-white cursor-pointer'
              : 'bg-white/10 text-white/30 cursor-not-allowed'
          }`}
        >
          {saving ? 'Guardando...' : `Guardar Favoritos${favorites.length < 3 ? ` (mínimo ${3 - favorites.length} más)` : ''}`}
        </button>
      </div>
    </div>
  );
}
